import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebaseConfig';
import FrozenAccountRecovery from './FrozenAccountRecovery';

/**
 * Component that protects routes by checking for frozen account state
 * Shows FrozenAccountRecovery component if account is frozen
 * Redirects unauthenticated users to the login page
 * NON-BLOCKING: Renders children immediately while checking status in background
 */
export default function FrozenAccountProtectedRoute({ children }) {
  const [user, setUser] = useState(null);
  const [isFrozen, setIsFrozen] = useState(null); // null = unknown, true = frozen, false = not frozen
  const [frozenData, setFrozenData] = useState(null);
  const [checksComplete, setChecksComplete] = useState(false);
  
  useEffect(() => {
    let previousUid = null;
    
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        previousUid = currentUser.uid;
        
        // Check for cached frozen status first
        const cacheKey = `frozen_status_${currentUser.uid}`;
        const cachedData = sessionStorage.getItem(cacheKey);
        
        if (cachedData) {
          try {
            const { isFrozen: cachedFrozen, frozenData: cachedFrozenData, timestamp } = JSON.parse(cachedData);
            // Cache is valid for 1 minute (shorter than other caches since this is critical)
            if (Date.now() - timestamp < 1 * 60 * 1000) {
              setIsFrozen(cachedFrozen);
              setFrozenData(cachedFrozenData);
              setChecksComplete(true);
              return;
            }
          } catch (error) {
            console.error('Error parsing cached frozen status:', error);
          }
        }
        
        // Check frozen status via API in background
        try {
          const response = await fetch('/api/checkFrozenStatus', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ uid: currentUser.uid }),
          });
          
          if (response.ok) {
            const { isFrozen: frozenStatus, frozenData: receivedFrozenData } = await response.json();
            setIsFrozen(frozenStatus);
            setFrozenData(receivedFrozenData);
            
            // Cache the result
            sessionStorage.setItem(cacheKey, JSON.stringify({
              isFrozen: frozenStatus,
              frozenData: receivedFrozenData,
              timestamp: Date.now()
            }));
          }
        } catch (error) {
          console.error('Error checking frozen status:', error);
        }
        setChecksComplete(true);
      } else {
        // User logged out while we were checking
        if (previousUid) {
          const cacheKey = `frozen_status_${previousUid}`;
          sessionStorage.removeItem(cacheKey);
        }
        setUser(null);
        setIsFrozen(null);
        setFrozenData(null);
        setChecksComplete(true);
      }
    });
    
    return () => unsubscribe();
  }, []);

  const handleRecoverySuccess = () => {
    setIsFrozen(false);
    setFrozenData(null);
    // Clear the cached frozen status since account is recovered
    if (user?.uid) {
      const cacheKey = `frozen_status_${user.uid}`;
      sessionStorage.removeItem(cacheKey);
    }
  };

  // Not logged in and checks complete - redirect to sign in
  if (checksComplete && !user) {
    window.location.href = '/';
    return null;
  }
  
  // Account is frozen (show recovery immediately when status is determined)
  if (user && isFrozen === true) {
    return (
      <FrozenAccountRecovery 
        frozenData={frozenData} 
        onSuccess={handleRecoverySuccess}
      />
    );
  }
  
  // Render children immediately (non-blocking) - will show content while checking or if not frozen
  return children;
} 